import React, { useState, useEffect } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../../ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../../ui/table";
import { Button } from "../../../ui/button";
import { Badge } from "../../../ui/badge";
import { Edit } from "lucide-react";
import { usePermissions } from "../../../../PermissionsProvider";
import { ConversationBuilderService } from "../../../../services/ConversationBuilderService";

interface ConversationTree {
  id: number;
  conversation_flow_name: string;
  conversation_flow_version: number;
  is_latest: boolean;
  created_at: string;
  application_name: string;
  intent_name: string;
  conversation_flow_count?: number;
}

interface LoadTreeModalProps {
  visible: boolean;
  onCancel: () => void;
  onLoadTree: (tree: ConversationTree) => void;
}

const LoadTreeModal: React.FC<LoadTreeModalProps> = ({
  visible,
  onCancel,
  onLoadTree,
}) => {
  
  const { companyId, apiKey } = usePermissions();
  const [trees, setTrees] = useState<ConversationTree[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!visible || companyId == null || !apiKey) return;

    setLoading(true);
    
    const response = ConversationBuilderService.listConversationTreesByCompany(companyId, apiKey);
    
    const subscription = response.subscribe({
      next: (data: ConversationTree[]) => {
        setTrees(data);
        setLoading(false);
      },
      error: (error: any) => {
        toast.error("Failed to load conversation trees");
        setLoading(false);
      }
    });

    // Cleanup subscription on unmount or when dependencies change
    return () => {
      subscription.unsubscribe();
    };
  }, [visible, companyId, apiKey]);

  const handleSelectTree = (tree: ConversationTree) => {
    onLoadTree(tree);
    onCancel(); // Close modal after selection
  };

  return (
    <Dialog open={visible} onOpenChange={onCancel}>
      <DialogContent className="sm:max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-lg font-gc-med text-foreground">
            Load Conversation Tree
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4">
          <p className="text-sm text-muted-foreground">
            Select a conversation tree to load into the editor
          </p>

          {loading ? (
            <div className="flex justify-center py-8">
              <div className="text-sm text-muted-foreground">Loading trees...</div>
            </div>
          ) : trees.length === 0 ? (
            <div className="text-center py-10">
              <p className="text-muted-foreground">No conversation trees found</p>
              <p className="text-xs text-muted-foreground mt-1">
                Create and save your first tree to see it here
              </p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="border-b border-border hover:bg-transparent">
                  <TableHead className="w-[40%]">Flow Name</TableHead>
                  <TableHead className="w-[15%]">Version</TableHead>
                  <TableHead className="w-[15%]">Paths</TableHead>
                  <TableHead className="w-[20%]">Created</TableHead>
                  <TableHead className="w-[10%]">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {trees.map((tree) => (
                  <TableRow
                    key={tree.id}
                    className="cursor-pointer hover:bg-accent/20 transition-colors border-b border-border/50"
                    onDoubleClick={() => handleSelectTree(tree)}
                  >
                    <TableCell className="py-4">
                      <div className="space-y-1">
                        <div className="text-sm font-gc-reg text-foreground">{tree.conversation_flow_name}</div>
                        <div className="text-xs text-muted-foreground">
                          {tree.application_name} - {tree.intent_name}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell className="py-4">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-gc-reg text-foreground">{tree.conversation_flow_version}</span>
                        {tree.is_latest && (
                          <Badge variant="secondary" className="bg-green-100 text-green-800 border-green-200">
                            Latest
                          </Badge>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="py-4">
                      <span className="text-sm font-gc-reg text-foreground">
                        {tree.conversation_flow_count ?? "-"} paths
                      </span>
                    </TableCell>
                    <TableCell className="py-4">
                      <span className="text-sm font-gc-reg text-foreground">
                        {new Date(tree.created_at).toLocaleDateString()}
                      </span>
                    </TableCell>
                    <TableCell className="py-4">
                      <Button
                        size="sm"
                        onClick={() => handleSelectTree(tree)}
                        className="gap-1"
                      >
                        <Edit className="w-3 h-3" />
                        Load
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
          
          {trees.length > 0 && (
            <div className="text-xs text-muted-foreground">
              💡 Tip: Double-click any row to load that tree
            </div>
          )}
        </div>
        
        <DialogFooter>
          <Button variant="outline" onClick={onCancel}>
            Cancel
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default LoadTreeModal; 